/** Construção geométrica -> `Scene`. */

import type {
  ConstructionSnapshot,
  EvaluationResult,
  GeoId,
  GeoObject,
} from '../geom/construction.ts';
import { isAnchored } from '../geom/construction.ts';
import { add } from '../geom/vec.ts';
import type { PointRole, Rect, Scene, Shape, StrokeRole } from './scene.ts';
import { boundsOf, scene } from './scene.ts';

export interface GeomSceneOptions {
  readonly title: string;
  readonly desc: string;
  /** Ids que pertencem ao enunciado (desenhados como `given`). */
  readonly givenIds: ReadonlySet<string>;
  /** Ids a destacar como resposta. */
  readonly resultIds?: ReadonlySet<string>;
  readonly snapshot?: ConstructionSnapshot;
  readonly grid?: { readonly step: number; readonly dots: boolean };
  readonly viewBox?: Rect;
  readonly showLabels?: boolean;
  /**
   * Envolve as formas de cada objeto num grupo com `key` = id do objeto. O
   * renderizador emite `data-key`, e assim a prancheta consegue achar no DOM o
   * elemento que acabou de nascer para anima-lo. Desligado por padrao: figuras
   * estaticas nao precisam da camada extra.
   */
  readonly keyed?: boolean;
}

export function constructionToScene(res: EvaluationResult, opts: GeomSceneOptions): Scene {
  const shapes: Shape[] = [];
  const objetos = [...res.objects.values()];

  // Limites primeiro: precisamos deles para recortar retas infinitas.
  const provisorio = shapesForBounds(objetos);
  const box = opts.viewBox ?? boundsOf(provisorio, 0.12);

  if (opts.grid) {
    shapes.push({ t: 'grid', step: opts.grid.step, extent: box, dots: opts.grid.dots });
  }

  // Ordem de pintura: auxiliares embaixo, dados no meio, resposta e pontos por cima.
  const linhas = objetos.filter((o) => o.kind !== 'point');
  const pontos = objetos.filter((o) => o.kind === 'point');

  const emitir = (o: GeoObject): void => {
    const partes = shapeFor(o, opts);
    if (opts.keyed) shapes.push({ t: 'group', children: partes, key: o.id as string });
    else shapes.push(...partes);
  };

  for (const o of linhas) emitir(o);
  for (const o of pontos) emitir(o);

  return scene(shapes, { a11y: { title: opts.title, desc: opts.desc }, viewBox: box });
}

function roleFor(id: GeoId, opts: GeomSceneOptions): StrokeRole {
  const key = id as string;
  if (opts.resultIds?.has(key)) return 'result';
  if (opts.givenIds.has(key)) return 'given';
  return 'construction';
}

function pointRoleFor(id: GeoId, opts: GeomSceneOptions): PointRole {
  const key = id as string;
  if (opts.resultIds?.has(key)) return 'result';
  if (opts.givenIds.has(key)) return 'given';
  // Distinção visual honesta entre um ponto derivado e um ponto largado a olho:
  // o aluno vê, enquanto constrói, se o que fez está ancorado nos dados.
  if (opts.snapshot && !isAnchored(opts.snapshot, id)) return 'free';
  return 'derived';
}

function shapeFor(o: GeoObject, opts: GeomSceneOptions): Shape[] {
  if (o.kind === 'point') {
    const s: Shape = o.name
      ? {
          t: 'point',
          p: o.pos,
          role: pointRoleFor(o.id, opts),
          name: opts.showLabels === false ? '' : o.name,
        }
      : { t: 'point', p: o.pos, role: pointRoleFor(o.id, opts) };
    return [s];
  }

  if (o.kind === 'circle') {
    return [{ t: 'circle', c: o.c, r: o.r, role: roleFor(o.id, opts) }];
  }

  // Retas do aluno ganham traço próprio, contínuo; os arcos continuam de construção.
  const base = roleFor(o.id, opts);
  const role: StrokeRole = base === 'construction' ? 'drawn' : base;
  if (o.span === 'segment') return [{ t: 'seg', a: o.p, b: o.q, role }];
  // SEMIRRETA E RETA SAEM COMO SEMIRRETA E RETA, e não como segmento já esticado.
  //
  // Antes, elas viravam `seg` com as pontas a 1,1 vez a diagonal do quadro a partir de
  // um ponto qualquer delas — o que não é recorte nenhum: com o ponto no meio do
  // quadro, o traço saía meia diagonal para fora. Na tela passava (há espaço em volta
  // da figura, e `svg.scene` deixa transbordar de propósito, para não cortar o halo de
  // um ponto na borda); no papel, onde as questões se encostam, o eixo de simetria de
  // uma construção riscava o enunciado de baixo.
  //
  // Devolvendo as formas próprias, quem apara é o renderizador, contra o quadro que
  // está valendo na hora do desenho — o que também acerta o zoom da prancheta.
  if (o.span === 'ray') return [{ t: 'ray', from: o.p, through: add(o.p, o.dir), role }];
  return [{ t: 'line', p: o.p, dir: o.dir, role }];
}

/** Formas simplificadas só para calcular limites (retas infinitas não contam). */
function shapesForBounds(objetos: readonly GeoObject[]): Shape[] {
  const out: Shape[] = [];
  for (const o of objetos) {
    if (o.kind === 'point') out.push({ t: 'point', p: o.pos, role: 'given' });
    else if (o.kind === 'circle') out.push({ t: 'circle', c: o.c, r: o.r, role: 'given' });
    else if (o.span === 'segment') out.push({ t: 'seg', a: o.p, b: o.q, role: 'given' });
    else out.push({ t: 'point', p: o.p, role: 'given' });
  }
  return out;
}
